import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import MasterDataMappingsForm from './MasterDataMappingsForm';
import DateCompareForm from './DateCompareForm';
import SameDayForm from './SameDayForm';
import MaxTotalDurationForm from './MaxTotalDurationForm';
import SumEqualsForm from './SumEqualsForm';
import AdditionalValidatorsForm from './AdditionalValidatorsForm';

/**
 * Top-level orchestrator for guided validation rules every production
 * DataElement may carry alongside its JSON Schema. Each sub-form owns its
 * own keyword; this panel decides which sections are visible.
 *
 * UX model:
 *   - A section renders only when its rule is present on the schema.
 *     "Present" means: non-null and (for collection-shaped values) non-empty.
 *   - Absent rules surface in an "Add validation rule" picker at the bottom
 *     so users can opt a section in without seeing four empty cards on
 *     every DE.
 *   - Each open section gets a "Remove" header action so an
 *     accidentally-added empty section can be opted back out without
 *     having to fill and clear every field.
 *
 * Data flow:
 *   - `value` is the editor record (one entry per CUSTOM_VALIDATOR_KEY).
 *     `null` means "rule not authored on this schema".
 *   - `onChange(next)` receives a fresh record on every edit. The parent
 *     is responsible for sanitizing before it reaches the schema.
 *   - `flattenedFieldOptions` is the optional list of dotted-path field
 *     names from the schema builder, used to populate `<datalist>` hints.
 */

const SECTION_META = {
	masterDataMappings: {
		label: 'Master Data Checks',
		addLabel: 'Master Data Checks',
		chipLabel: 'Master data',
		description: 'Check selected fields against approved master-data records.',
	},
	dateCompare: {
		label: 'Time In Before Time Out',
		addLabel: 'Time In Before Time Out',
		chipLabel: 'Time in before time out',
		description: 'For attendance rows, make sure Time In is earlier than Time Out.',
	},
	sameDay: {
		label: 'Same Day Attendance',
		addLabel: 'Same Day Attendance',
		chipLabel: 'Same-day attendance',
		description: 'Make sure all attendance dates fall on the same calendar day.',
	},
	maxTotalDuration: {
		label: 'Max Total Duration',
		addLabel: 'Max Total Duration',
		chipLabel: 'Max total duration',
		description: 'Make sure total Time In to Time Out duration does not exceed the allowed hours.',
	},
	sumEquals: {
		label: 'Totals Must Match',
		addLabel: 'Totals Must Match',
		chipLabel: 'Totals must match',
		description: 'Make sure selected numbers add up to the expected total.',
	},
	additional: {
		label: 'Other Validation Rules',
		addLabel: 'Other Validation Rule',
		chipLabel: 'Other rule',
		description: 'Advanced rules that do not have a guided form yet.',
	},
};

const ORDERED_KEYS = ['masterDataMappings', 'dateCompare', 'sameDay', 'maxTotalDuration', 'sumEquals', 'additional'];

const EMPTY_SEED = {
	masterDataMappings: {},
	dateCompare: [],
	sameDay: {},
	maxTotalDuration: {},
	sumEquals: {},
	additional: [],
};

function isPresent(key, value) {
	if (value == null) return false;
	if (key === 'masterDataMappings') return typeof value === 'object' && Object.keys(value).length > 0;
	if (key === 'dateCompare') return Array.isArray(value) && value.length > 0;
	if (key === 'sameDay') return typeof value === 'object' && Object.keys(value).length > 0;
	if (key === 'maxTotalDuration') return typeof value === 'object' && Object.keys(value).length > 0;
	if (key === 'sumEquals') return typeof value === 'object' && Object.keys(value).length > 0;
	if (key === 'additional') return Array.isArray(value) && value.length > 0;
	return false;
}

/**
 * "Visible" is broader than "present": once the user adds a section via
 * the picker we keep it on screen (with empty editable fields) until they
 * either author content or hit "Remove". `optedIn` is the set
 * of keys the user opted in this session — orthogonal to whatever the
 * sanitized schema value happens to be after debouncing.
 */
function isVisible(key, value, optedIn) {
	if (optedIn.has(key)) return true;
	return isPresent(key, value);
}

function getCountLabel(key, value) {
	if (!isPresent(key, value)) return 'Draft';
	if (key === 'masterDataMappings') {
		const count = Object.keys(value).length;
		return `${count} ${count === 1 ? 'mapping' : 'mappings'}`;
	}
	if (key === 'dateCompare') {
		return `${value.length} ${value.length === 1 ? 'rule' : 'rules'}`;
	}
	if (key === 'additional') {
		return `${value.length} ${value.length === 1 ? 'rule' : 'rules'}`;
	}
	return 'Configured';
}

export default function CustomValidatorsPanel({
	value,
	onChange,
	disabled = false,
	flattenedFieldOptions = [],
	masterDataTableOptions = [],
}) {
	const safe = value || {};
	const [optedIn, setOptedIn] = useState(() => new Set());
	const [pickerSelection, setPickerSelection] = useState('');
	const internalChangeRef = useRef(false);
	const previousValueRef = useRef(value);

	useEffect(() => {
		if (previousValueRef.current === value) return;

		if (internalChangeRef.current) {
			internalChangeRef.current = false;
		} else {
			setOptedIn(new Set());
			setPickerSelection('');
		}

		previousValueRef.current = value;
	}, [value]);

	const set = (key, next) => {
		internalChangeRef.current = true;
		onChange({ ...safe, [key]: next });
	};

	const optIn = (key) => {
		setOptedIn((prev) => {
			const nextSet = new Set(prev);
			nextSet.add(key);
			return nextSet;
		});
		// Seed with an editable-empty value so the form renders.
		if (!isPresent(key, safe[key])) {
			set(key, EMPTY_SEED[key]);
		}
	};

	const optOut = (key) => {
		setOptedIn((prev) => {
			if (!prev.has(key)) return prev;
			const nextSet = new Set(prev);
			nextSet.delete(key);
			return nextSet;
		});
		// Always null on opt-out so the schema drops the key.
		set(key, null);
	};

	const handleAdd = () => {
		if (!pickerSelection) return;
		optIn(pickerSelection);
		setPickerSelection('');
	};

	const summary = useMemo(() => {
		const present = [];
		if (isPresent('masterDataMappings', safe.masterDataMappings)) {
			present.push(`${SECTION_META.masterDataMappings.chipLabel} (${Object.keys(safe.masterDataMappings).length})`);
		}
		if (isPresent('dateCompare', safe.dateCompare)) {
			present.push(`${SECTION_META.dateCompare.chipLabel} (${safe.dateCompare.length})`);
		}
		if (isPresent('sameDay', safe.sameDay)) present.push(SECTION_META.sameDay.chipLabel);
		if (isPresent('maxTotalDuration', safe.maxTotalDuration)) present.push(SECTION_META.maxTotalDuration.chipLabel);
		if (isPresent('sumEquals', safe.sumEquals)) present.push(SECTION_META.sumEquals.chipLabel);
		if (isPresent('additional', safe.additional)) {
			for (const entry of safe.additional) {
				if (entry?.key) present.push(`${SECTION_META.additional.chipLabel}: ${entry.key}`);
			}
		}
		return present;
	}, [safe]);

	const visibleKeys = ORDERED_KEYS.filter((k) => isVisible(k, safe[k], optedIn));
	const absentKeys = ORDERED_KEYS.filter((k) => !isVisible(k, safe[k], optedIn));

	const renderSectionHeader = (key) => (
		<div className="dsv-cv-section-actions">
			<span className="dsv-cv-section-actions__note">Validation rule</span>
			{!disabled && (
				<button
					type="button"
					className="dsv-cv-section-actions__remove"
					onClick={() => optOut(key)}
					data-testid={`cv-section-remove-${key}`}
				>
					Remove
				</button>
			)}
		</div>
	);

	const renderSectionSummary = (key) => (
		<summary className="dsv-modal__constraints-toggle dsv-cv-section__summary">
			<span className="dsv-cv-section__summary-copy">
				<span className="dsv-cv-section__title">{SECTION_META[key].label}</span>
				<span className="dsv-cv-section__description">{SECTION_META[key].description}</span>
			</span>
			<span className="dsv-cv-section__badge">
				{getCountLabel(key, safe[key])}
			</span>
		</summary>
	);

	const renderSection = (key, testId, children) => (
		<details
			className={`dsv-modal__constraints-section dsv-cv-section dsv-cv-section--${key}`}
			open
			data-testid={testId}
		>
			{renderSectionSummary(key)}
			<div className="dsv-cv-section__body">
				{renderSectionHeader(key)}
				{children}
			</div>
		</details>
	);

	const renderSummary = () => {
		if (summary.length === 0) {
			return (
				<div className="dsv-custom-validators__empty-state">
					No extra validation rules are defined on this schema yet.
				</div>
			);
		}

		return (
			<div className="dsv-custom-validators__summary" data-testid="cv-summary">
				<span className="dsv-custom-validators__summary-label">In use</span>
				<div className="dsv-custom-validators__chips">
					{summary.map((item, idx) => (
						<span key={`${item}-${idx}`} className="dsv-custom-validators__chip">
							{item}
						</span>
					))}
				</div>
			</div>
		)
	};

	return (
		<div className="dsv-custom-validators" data-testid="custom-validators-panel">
			<div className="dsv-custom-validators__header">
				<div className="dsv-custom-validators__header-main">
					<div>
						<span className="dsv-custom-validators__eyebrow">Validation rules</span>
						<h3 className="dsv-custom-validators__title">Custom Validations</h3>
						<p className="dsv-custom-validators__hint">
							Use these rules to explain business checks that the normal field
							type, mandatory, and format settings cannot express.
						</p>
					</div>
					<div className="dsv-custom-validators__metric">
						<strong>{visibleKeys.length}</strong>
						<span>{visibleKeys.length === 1 ? 'rule shown' : 'rules shown'}</span>
					</div>
				</div>
				{renderSummary()}
			</div>

			{visibleKeys.includes('masterDataMappings') && (
				renderSection('masterDataMappings', 'cv-section-master-data-mappings',
					<MasterDataMappingsForm
						value={safe.masterDataMappings}
						onChange={(next) => set('masterDataMappings', next)}
						disabled={disabled}
						fieldOptions={flattenedFieldOptions}
						tableOptions={masterDataTableOptions}
					/>
				)
			)}

			{visibleKeys.includes('dateCompare') && (
				renderSection('dateCompare', 'cv-section-date-compare',
					<DateCompareForm
						value={safe.dateCompare}
						onChange={(next) => set('dateCompare', next)}
						disabled={disabled}
						fieldOptions={flattenedFieldOptions}
					/>
				)
			)}

			{visibleKeys.includes('sameDay') && (
				renderSection('sameDay', 'cv-section-same-day',
					<SameDayForm
						value={safe.sameDay}
						onChange={(next) => set('sameDay', next)}
						disabled={disabled}
						fieldOptions={flattenedFieldOptions}
					/>
				)
			)}

			{visibleKeys.includes('maxTotalDuration') && (
				renderSection('maxTotalDuration', 'cv-section-max-total-duration',
					<MaxTotalDurationForm
						value={safe.maxTotalDuration}
						onChange={(next) => set('maxTotalDuration', next)}
						disabled={disabled}
						fieldOptions={flattenedFieldOptions}
					/>
				)
			)}

			{visibleKeys.includes('sumEquals') && (
				renderSection('sumEquals', 'cv-section-sum-equals',
					<SumEqualsForm
						value={safe.sumEquals}
						onChange={(next) => set('sumEquals', next)}
						disabled={disabled}
						fieldOptions={flattenedFieldOptions}
					/>
				)
			)}

			{visibleKeys.includes('additional') && (
				renderSection('additional', 'cv-section-additional',
					<AdditionalValidatorsForm
						value={safe.additional}
						onChange={(next) => set('additional', next)}
						disabled={disabled}
					/>
				)
			)}

			{!disabled && absentKeys.length > 0 && (
				<div className="dsv-cv-add-picker" data-testid="cv-add-validator-row">
					<div className="dsv-cv-add-picker__copy">
						<span className="dsv-cv-add-picker__label">Add validation rule</span>
						<span className="dsv-cv-add-picker__hint">
							Only add rules that should be checked for this Data Element.
						</span>
					</div>
					<div className="dsv-cv-add-picker__controls">
						<select
							className="dsv-modal__select dsv-cv-add-picker__select"
							value={pickerSelection}
							onChange={(e) => setPickerSelection(e.target.value)}
							data-testid="cv-add-validator-picker"
						>
							<option value="">Choose a rule…</option>
							{absentKeys.map((k) => (
								<option key={k} value={k}>{SECTION_META[k].addLabel}</option>
							))}
						</select>
						<button
							type="button"
							className="dsv-cv-add-picker__button"
							onClick={handleAdd}
							disabled={!pickerSelection}
							data-testid="cv-add-validator-confirm"
						>
							Add Rule
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

CustomValidatorsPanel.propTypes = {
	value: PropTypes.object,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	flattenedFieldOptions: PropTypes.arrayOf(PropTypes.string),
	masterDataTableOptions: PropTypes.arrayOf(PropTypes.string),
};
